// web/frontend/src/pages/admin/Operations.jsx
import React, { useState, useEffect } from 'react';
import { Zap, DollarSign, 
  Shield, ToggleLeft, ToggleRight, RefreshCw, UserPlus, LogIn, Clock,
  Search, Brain, Download, CreditCard, Globe, SlidersHorizontal, Calendar
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const ICON_MAP = {
  dollar: <DollarSign size={20} />,
  zap: <Zap size={20} />,
  'user-plus': <UserPlus size={20} />,
  'log-in': <LogIn size={20} />,
  'chrome': <Globe size={20} />,
  'search': <Search size={20} />,
  'brain': <Brain size={20} />,
  'download': <Download size={20} />,
  'credit-card': <CreditCard size={20} />,
  'shield': <Shield size={20} />,
  'clock': <Clock size={20} />,
  'calendar': <Calendar size={20} />,
};

const CATEGORY_LABELS = {
  tokens: '💰 Token Pricing',
  development: '🧪 Development',
  access: 'Access Control',
  operations: 'Platform Operations',
  billing: 'Billing',
  security: 'Security',
};

export default function Operations() {
  const [settings, setSettings] = useState([]);
  const [launchDate, setLaunchDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [savingDate, setSavingDate] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/operations/settings');
      setSettings(res.data.settings || []);
      const ld = (res.data.settings || []).find(s => s.key === 'launch_date');
      if (ld) setLaunchDate(ld.value || '');
      // Also load launch date
      try {
        const d = await api.get('/api/admin/launch-date');
        setLaunchDate(d.data.launch_date || '');
      } catch {}
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (setting) => {
    setToggling(setting.key);
    try {
      await api.post('/api/admin/operations/toggle', {
        setting: setting.key,
        value: !setting.value,
      });
      setSettings(prev =>
        prev.map(s => s.key === setting.key ? { ...s, value: !s.value } : s)
      );
      toast.success(`${setting.label} ${!setting.value ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to toggle setting');
    } finally {
      setToggling(null);
    }
  };

  const handleSaveDate = async () => {
    if (!launchDate.trim()) {
      toast.error('Please enter a launch date');
      return;
    }
    setSavingDate(true);
    try {
      const res = await api.post('/api/admin/launch-date', { launch_date: launchDate.trim() });
      toast.success(res.data.message || 'Launch date saved');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to save launch date';
      toast.error(msg);
      console.error('Save launch date error:', err.response?.data || err);
    } finally {
      setSavingDate(false);
    }
  };

  const groupedSettings = settings.reduce((acc, setting) => {
    const cat = setting.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(setting);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 'var(--space-2xl)', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <h2 style={{
            color: 'var(--text-primary)', fontSize: 24, fontWeight: 700, margin: '0 0 4px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <SlidersHorizontal size={26} color="var(--accent-primary)" />
            System Operations
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
            Toggle system‑wide features on or off. Changes take effect immediately.
          </p>
        </div>
        <button onClick={loadSettings} style={{
          padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-primary)',
          background: 'transparent', color: 'var(--text-secondary)',
          cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
          transition: 'background 0.2s',
        }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Settings grouped by category */}
      {Object.entries(groupedSettings).map(([category, items]) => (
        <div key={category} style={{ marginBottom: 'var(--space-2xl)' }}>
          <h3 style={{
            color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            marginBottom: 'var(--space-md)', paddingLeft: 4,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'var(--accent-primary)', display: 'inline-block',
            }} />
            {CATEGORY_LABELS[category] || category}
          </h3>

          {/* Grid: 2 columns on desktop, 1 on mobile */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: 'var(--space-md)',
          }}>
            {items.filter(s => s.key !== 'launch_date').map(setting => (
              <div key={setting.key} style={{
                background: 'linear-gradient(145deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)',
                border: `1px solid ${setting.value ? 'rgba(63,185,80,0.25)' : 'rgba(248,81,73,0.2)'}`,
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--space-lg)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.25s ease',
                boxShadow: setting.value
                  ? '0 0 15px rgba(63,185,80,0.1)'
                  : '0 0 15px rgba(248,81,73,0.05)',
              }}>
                {/* Subtle scan line overlay */}
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.01) 2px, rgba(255,255,255,0.01) 4px)',
                  pointerEvents: 'none',
                }} />

                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 12, position: 'relative', zIndex: 1,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 14,
                      background: setting.value
                        ? 'linear-gradient(135deg, rgba(63,185,80,0.15), rgba(63,185,80,0.05))'
                        : 'linear-gradient(135deg, rgba(248,81,73,0.15), rgba(248,81,73,0.05))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: setting.value ? 'var(--success)' : 'var(--error)',
                      flexShrink: 0,
                      border: `1px solid ${setting.value ? 'rgba(63,185,80,0.3)' : 'rgba(248,81,73,0.3)'}`,
                    }}>
                      {ICON_MAP[setting.icon] || <Shield size={20} />}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>
                        {setting.label}
                      </div>
                      <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 2, lineHeight: 1.4 }}>
                        {setting.description}
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => handleToggle(setting)}
                    disabled={toggling === setting.key}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 10,
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                      fontSize: 13,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      background: setting.value
                        ? 'linear-gradient(135deg, var(--success), #2ea44f)'
                        : 'linear-gradient(135deg, var(--error), #c62828)',
                      color: '#fff',
                      opacity: toggling === setting.key ? 0.5 : 1,
                      flexShrink: 0,
                      transition: 'transform 0.15s, box-shadow 0.15s',
                      boxShadow: setting.value
                        ? '0 2px 8px rgba(63,185,80,0.3)'
                        : '0 2px 8px rgba(248,81,73,0.3)',
                    }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    {setting.value ? (
                      <><ToggleRight size={18} /> Enabled</>
                    ) : (
                      <><ToggleLeft size={18} /> Disabled</>
                    )}
                  </button>
                </div>

                {/* Status indicator bar */}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0, height: 3,
                  background: setting.value
                    ? 'linear-gradient(90deg, var(--success), transparent)'
                    : 'linear-gradient(90deg, var(--error), transparent)',
                  opacity: 0.6,
                }} />
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Launch Date Setting,  only shows when Coming Soon is ON */}
      {settings.find(s => s.key === 'coming_soon')?.value && (
        <div style={{
          background: 'linear-gradient(145deg, var(--bg-secondary), var(--bg-tertiary))',
          border: '2px solid var(--accent-amber)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-xl)',
          marginBottom: 'var(--space-xl)',
        }}>
          <h4 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={18} color="var(--accent-amber)" /> Launch Countdown Date
          </h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: 12, margin: '0 0 16px' }}>
            Select the exact date and time the countdown timer should target. This shows on the coming soon page.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <input
              type="datetime-local"
              value={launchDate}
              onChange={(e) => setLaunchDate(e.target.value)}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 10,
                border: '1px solid var(--border-primary)',
                background: 'var(--input-bg)', color: 'var(--text-primary)',
                fontSize: 14, outline: 'none', fontFamily: 'inherit',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border-primary)'}
            />
            <button onClick={handleSaveDate} disabled={savingDate} style={{
              padding: '10px 24px', borderRadius: 10, border: 'none',
              background: savingDate ? 'var(--bg-tertiary)' : 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: savingDate ? 'var(--text-tertiary)' : '#0B1121',
              cursor: savingDate ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 700,
              whiteSpace: 'nowrap', transition: 'all 0.2s',
            }}>
              {savingDate ? 'Saving...' : 'Set Launch Date'}
            </button>
          </div>
          {launchDate && (
            <p style={{ color: 'var(--success)', fontSize: 11, marginTop: 8 }}>
              Countdown target: {new Date(launchDate).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })}
            </p>
          )}
        </div>
      )}

      {/* Information panel */}
      <div style={{
        background: 'linear-gradient(145deg, var(--bg-secondary), var(--bg-tertiary))',
        border: '1px solid var(--border-primary)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-xl)',
        marginTop: 'var(--space-xl)',
        position: 'relative',
      }}>
        <h4 style={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>
          How Toggles Work
        </h4>
        <ul style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.8, margin: 0, paddingLeft: 18 }}>
          <li><strong>Coming Soon Mode:</strong> Shows a branded coming soon page to all visitors. Admins can still access the dashboard via direct API/login routes.</li>
          <li><strong>Registration Off:</strong> New users cannot sign up. Existing users unaffected.</li>
          <li><strong>Login Off:</strong> All non‑admin users are blocked from logging in. Admins can still log in.</li>
          <li><strong>Search Off:</strong> Running searches complete, but new searches are rejected.</li>
          <li><strong>Payments Off:</strong> Checkout pages return a maintenance message.</li>
          <li><strong>Rate Limit Test Mode:</strong> When ON, localhost is NOT exempt from rate limiting. Use for testing brute‑force protection.</li>
          <li>All changes are logged in the audit trail.</li>
        </ul>
      </div>
    </div>
  );
}